/**
 * Registration, password recovery and e-mail confirmation.
 *
 * All three share one property that shapes every screen here: **the server's
 * answer is deliberately uninformative**, and the interface must not undo
 * that. Registering an address that already exists, and requesting a reset for
 * an address with no account, both answer exactly as the successful case does.
 * A screen that said "we sent you a link" only when the account existed would
 * hand an anonymous visitor a way to test which addresses are registered —
 * turning a careful server-side decision into a client-side leak.
 *
 * So each screen shows the same confirmation regardless, and the wording says
 * "if that address…" rather than "we sent…", because telling the user the
 * truth about the uncertainty is better than implying a delivery that may not
 * have happened.
 */

import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { z } from "zod";
import { Alert, Button, Card, Input, LoadingState } from "@hardauto/core-ui";
import { Field, applyServerErrors } from "@hardauto/core-forms";
import { ApiError, NetworkError, type ApiClient } from "@hardauto/core-api";

import { NAMESPACE } from "../i18n";

/** i18next's own function type.
 *
 * A hand-rolled `(key: string, options?: object) => string` cannot express its
 * overloads under `exactOptionalPropertyTypes`, and writing one that almost
 * fits produces an error at every call site rather than a useful abstraction. */
type Translate = TFunction<typeof NAMESPACE>;

function describe(error: unknown, t: Translate, invitation = false): string {
  if (error instanceof NetworkError) return t("error.offline");
  if (!(error instanceof ApiError)) return t("error.unexpected");
  if (error.type === "rate_limited") {
    return t("error.rateLimited", { seconds: error.retryAfterSeconds ?? 60 });
  }
  if (error.type === "invalid_or_expired_token") {
    // Somebody with an expired invitation cannot "request a new link": they never had
    // a password to forget. Only whoever invited them can send another, and the
    // message says so.
    return t(invitation ? "resetPassword.invitationExpired" : "resetPassword.linkExpired");
  }
  return error.message;
}

// --- Registration ------------------------------------------------------------

const registerSchema = z.object({
  email: z.string().min(1),
  password: z.string().min(1),
  display_name: z.string().max(200).optional(),
});

type RegisterValues = z.infer<typeof registerSchema>;

export function RegisterScreen({ client, signInHref }: { client: ApiClient; signInHref?: string }) {
  const { t } = useTranslation(NAMESPACE);
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<RegisterValues>({ resolver: zodResolver(registerSchema) });

  if (sent) {
    return (
      <Card title={t("register.checkYourEmail")}>
        {/* Deliberately identical whether or not the address was already
            registered. The server answers the same; saying otherwise here
            would leak exactly what it refused to. */}
        <Alert tone="success">{t("register.sent")}</Alert>
        {signInHref ? <a href={signInHref}>{t("register.haveAccount")}</a> : null}
      </Card>
    );
  }

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await client.request(client.anonymousPath("/register/end-customer"), {
        method: "POST",
        anonymous: true,
        body: values,
      });
      setSent(true);
    } catch (error) {
      setFormError(describe(error, t));
      if (error instanceof ApiError) {
        applyServerErrors(error, form.setError, ["email", "password", "display_name"]);
      }
    }
  });

  return (
    <Card title={t("register.title")} description={t("register.description")}>
      <form onSubmit={(event) => void submit(event)} noValidate>
        {formError ? (
          <Alert tone="danger" title={t("register.failed")}>
            {formError}
          </Alert>
        ) : null}

        <Field label={t("field.email")} error={form.formState.errors.email?.message} required>
          {(props) => (
            <Input {...props} {...form.register("email")} type="email" autoComplete="email" />
          )}
        </Field>

        <Field label={t("field.displayName")} error={form.formState.errors.display_name?.message}>
          {(props) => <Input {...props} {...form.register("display_name")} autoComplete="name" />}
        </Field>

        <Field label={t("field.password")} error={form.formState.errors.password?.message} required>
          {(props) => (
            <Input
              {...props}
              {...form.register("password")}
              type="password"
              // `new-password` is what makes a password manager OFFER to
              // generate one. `current-password` here would make it try to
              // fill an existing credential instead.
              autoComplete="new-password"
            />
          )}
        </Field>

        <Button
          type="submit"
          variant="primary"
          busy={form.formState.isSubmitting}
          busyLabel={t("register.submitting")}
        >
          {t("register.submit")}
        </Button>
      </form>
    </Card>
  );
}

// --- Forgotten password ------------------------------------------------------

const forgotSchema = z.object({ email: z.string().min(1) });

export function ForgotPasswordScreen({
  client,
  signInHref,
}: {
  client: ApiClient;
  signInHref?: string;
}) {
  const { t } = useTranslation(NAMESPACE);
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<{ email: string }>({ resolver: zodResolver(forgotSchema) });

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await client.request(client.anonymousPath("/password-reset/request"), {
        method: "POST",
        anonymous: true,
        body: values,
      });
    } catch (error) {
      // A rate limit is the one failure worth showing: it is about the caller,
      // not about whether the account exists, and hiding it would leave the
      // user pressing a button that silently does nothing.
      if (error instanceof ApiError && error.type === "rate_limited") {
        setFormError(describe(error, t));
        return;
      }
      // Everything else is swallowed on purpose. Reporting "no such account"
      // is precisely the leak the uniform server response exists to prevent.
    }
    setSent(true);
  });

  if (sent) {
    return (
      <Card title={t("forgotPassword.title")}>
        <Alert tone="success">{t("forgotPassword.sent")}</Alert>
        {signInHref ? <a href={signInHref}>{t("forgotPassword.backToSignIn")}</a> : null}
      </Card>
    );
  }

  return (
    <Card title={t("forgotPassword.title")} description={t("forgotPassword.description")}>
      <form onSubmit={(event) => void submit(event)} noValidate>
        {formError ? <Alert tone="danger">{formError}</Alert> : null}

        <Field label={t("field.email")} error={form.formState.errors.email?.message} required>
          {(props) => (
            <Input {...props} {...form.register("email")} type="email" autoComplete="email" />
          )}
        </Field>

        <Button
          type="submit"
          variant="primary"
          busy={form.formState.isSubmitting}
          busyLabel={t("forgotPassword.submitting")}
        >
          {t("forgotPassword.submit")}
        </Button>
      </form>
    </Card>
  );
}

// --- Reset ------------------------------------------------------------------

const resetSchema = z
  .object({
    password: z.string().min(1),
    confirm: z.string().min(1),
  })
  // Checked here rather than server-side because the server never sees the
  // confirmation field — it exists only to catch a typo before one is stored.
  .refine((values) => values.password === values.confirm, {
    path: ["confirm"],
    message: "mismatch",
  });

type ResetValues = z.infer<typeof resetSchema>;

export function ResetPasswordScreen({
  client,
  token,
  onDone,
  invitation = false,
}: {
  client: ApiClient;
  /** From the e-mailed link. */
  token: string;
  onDone: () => void;
  /**
   * The link was an invitation, not a reset: this is the person's first password. The
   * wording changes ("welcome", not "choose a *new* password"); the endpoint does not.
   */
  invitation?: boolean;
}) {
  const { t } = useTranslation(NAMESPACE);
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<ResetValues>({ resolver: zodResolver(resetSchema) });

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await client.request(client.anonymousPath("/password-reset/confirm"), {
        method: "POST",
        anonymous: true,
        body: { token, password: values.password },
      });
      onDone();
    } catch (error) {
      setFormError(describe(error, t, invitation));
      if (error instanceof ApiError) applyServerErrors(error, form.setError, ["password"]);
    }
  });

  const confirmError = form.formState.errors.confirm?.message;

  return (
    <Card title={t(invitation ? "resetPassword.invitationTitle" : "resetPassword.title")}>
      <form onSubmit={(event) => void submit(event)} noValidate>
        {invitation ? <p>{t("resetPassword.invitationIntro")}</p> : null}
        {formError ? (
          <Alert tone="danger" title={t("resetPassword.failed")}>
            {formError}
          </Alert>
        ) : null}

        <Field
          label={t("field.newPassword")}
          error={form.formState.errors.password?.message}
          required
        >
          {(props) => (
            <Input
              {...props}
              {...form.register("password")}
              type="password"
              autoComplete="new-password"
            />
          )}
        </Field>

        <Field
          label={t("field.confirmPassword")}
          // The schema stores a key, not a sentence, so the message is
          // translated rather than hard-coded in English inside a validator.
          error={confirmError === "mismatch" ? t("resetPassword.mismatch") : confirmError}
          required
        >
          {(props) => (
            <Input
              {...props}
              {...form.register("confirm")}
              type="password"
              autoComplete="new-password"
            />
          )}
        </Field>

        <Button
          type="submit"
          variant="primary"
          busy={form.formState.isSubmitting}
          busyLabel={t("resetPassword.submitting")}
        >
          {t(invitation ? "resetPassword.invitationSubmit" : "resetPassword.submit")}
        </Button>
      </form>
    </Card>
  );
}

// --- E-mail confirmation -----------------------------------------------------

type VerifyState = "checking" | "done" | "failed";

export function VerifyEmailScreen({
  client,
  token,
  signInHref,
}: {
  client: ApiClient;
  token: string;
  signInHref?: string;
}) {
  const { t } = useTranslation(NAMESPACE);
  const [state, setState] = useState<VerifyState>("checking");

  /**
   * Which token has already been submitted.
   *
   * A ref, not a `cancelled` flag, and the difference is the whole point.
   * React's StrictMode mounts an effect, unmounts it, and mounts it again in
   * development — deliberately, to surface exactly this. A `cancelled` flag
   * stops the second *state update* but not the second *request*, and this
   * request spends a single-use token: the first call consumed it, the second
   * was told "already used", and the second one won. Every registration ended
   * on "that link has expired", while the account was in fact confirmed.
   *
   * Found by the end-to-end suite. It cannot be caught below this level,
   * because it needs a real double-mount against a real single-use token.
   */
  const submitted = useRef<string | null>(null);

  useEffect(() => {
    if (submitted.current === token) return;
    submitted.current = token;

    void (async () => {
      try {
        await client.request(client.anonymousPath("/email/verify"), {
          method: "POST",
          anonymous: true,
          body: { token },
        });
        setState("done");
      } catch {
        // The token is single-use, so "already used" and "expired" are the
        // same message to the user: request another link. Distinguishing them
        // would only tell a stranger holding the link which case it is.
        setState("failed");
      }
    })();

    // No cleanup, and no `cancelled` flag — deliberately.
    //
    // The usual pattern suppresses the state update when the effect is torn
    // down, which is right for a fetch that can simply be repeated. This one
    // cannot: the request has already spent the token, so discarding its
    // result leaves the screen saying "confirming…" forever while the account
    // is in fact confirmed. That was the second half of this bug, and it only
    // appeared once the first half was fixed.
    //
    // Setting state after an unmount is harmless in React 18 and later — the
    // update is dropped, without a warning.
  }, [client, token]);

  if (state === "checking") return <LoadingState label={t("verifyEmail.checking")} />;

  return (
    <Card title={t("verifyEmail.title")}>
      {state === "done" ? (
        <Alert tone="success">{t("verifyEmail.done")}</Alert>
      ) : (
        <Alert tone="danger">{t("verifyEmail.failed")}</Alert>
      )}
      {signInHref ? <a href={signInHref}>{t("verifyEmail.signIn")}</a> : null}
    </Card>
  );
}
